import React, { useState, useEffect, useCallback } from 'react';
import { X, Gamepad2, Crown, Clock, Trophy, Check, Loader2, Shield, ChevronDown, Medal, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import axios from 'axios';
import { API_URL } from '../config/api';
import { toast } from 'sonner';

const formatMs = (ms) => {
  if (!ms) return '—';
  const d = new Date(ms);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
    hour12: true,
  });
};

const getRankIcon = (rank) => {
  if (rank === 1) return <Crown className="h-4 w-4 text-amber-400" />;
  if (rank === 2) return <Medal className="h-4 w-4 text-gray-300" />;
  if (rank === 3) return <Medal className="h-4 w-4 text-amber-700" />;
  return <span className="text-[11px] text-gray-500 font-mono">{rank}</span>;
};

const LastPersonStanding = ({ isOpen, onClose, clubId, item, user }) => {
  const showName = item?.title || '';
  const posterPath = item?.poster_path || '';
  const tmdbId = item?.tmdb_id || 0;

  const [game, setGame] = useState(null);
  const [userEntry, setUserEntry] = useState(null);
  const [cast, setCast] = useState([]);
  const [castSource, setCastSource] = useState('');
  const [leaderboard, setLeaderboard] = useState(null);
  const [gameResult, setGameResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [selectedCast, setSelectedCast] = useState(null);
  const [survivors, setSurvivors] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminMode, setAdminMode] = useState('auto');
  const [countdown, setCountdown] = useState(null);

  const isAdmin = game && game.admin_user_id === user?.id;
  const isCompleted = game?.status === 'completed';
  const isPending = game?.status === 'pending_results';
  const hasSubmitted = !!userEntry;

  // Countdown timer
  const airDatetime = game?.episode_air_datetime;
  const predictionsLocked = countdown !== null && countdown <= 0;

  useEffect(() => {
    if (!airDatetime) { setCountdown(null); return; }
    const calcRemaining = () => {
      try {
        const airMs = new Date(airDatetime).getTime();
        return Math.floor((airMs - Date.now()) / 1000);
      } catch { return null; }
    };
    setCountdown(calcRemaining());
    const interval = setInterval(() => setCountdown(calcRemaining()), 1000);
    return () => clearInterval(interval);
  }, [airDatetime]);

  const formatCountdown = (seconds) => {
    if (seconds === null) return '';
    if (seconds <= 0) return 'Episode has aired — Predictions locked';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const parts = [];
    if (d > 0) parts.push(`${d}d`);
    parts.push(`${h}h`);
    parts.push(`${String(m).padStart(2, '0')}m`);
    parts.push(`${String(s).padStart(2, '0')}s`);
    return parts.join(' ');
  };

  const loadGame = useCallback(async () => {
    if (!isOpen || !clubId || !user?.id) return;
    setLoading(true);
    try {
      // Check for existing game for this show
      const res = await axios.get(`${API_URL}/api/club-games/movie`, {
        params: { club_id: clubId, show_id: String(tmdbId), game_type: 'last_person_standing' }
      });

      if (res.data.game) {
        setGame(res.data.game);
        const [detailRes, castRes] = await Promise.all([
          axios.get(`${API_URL}/api/club-games/${res.data.game.id}?user_id=${user.id}`),
          axios.get(`${API_URL}/api/club-games/${res.data.game.id}/cast`),
        ]);
        setGame(detailRes.data.game);
        setUserEntry(detailRes.data.user_entry);
        setCast(castRes.data.cast || []);
        setCastSource(castRes.data.source || '');

        if (detailRes.data.game.status === 'completed') {
          const lbRes = await axios.get(`${API_URL}/api/club-games/${res.data.game.id}/leaderboard`);
          setLeaderboard(lbRes.data.leaderboard || []);
          setGameResult(lbRes.data.result);
        }
      } else {
        setGame(null);
      }
    } catch (err) {
      console.error('Load game error:', err);
    } finally {
      setLoading(false);
    }
  }, [isOpen, clubId, tmdbId, user]);

  useEffect(() => {
    if (isOpen) {
      setGame(null); setUserEntry(null); setCast([]); setLeaderboard(null); setGameResult(null);
      setSelectedCast(null); setWinnerSelect(null); setShowAdminPanel(false);
      loadGame();
    }
  }, [isOpen, loadGame]);

  const handleCreateGame = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/club-games/create`, null, {
        params: {
          club_id: clubId, user_id: user.id,
          show_id: String(tmdbId), show_name: showName,
          game_type: 'last_person_standing',
        }
      });
      setGame(res.data.game);
      if (res.data.created) {
        toast.success('Game created! You are the administrator.');
      }
      const castRes = await axios.get(`${API_URL}/api/club-games/${res.data.game.id}/cast`);
      setCast(castRes.data.cast || []);
      setCastSource(castRes.data.source || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create game');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitPrediction = async () => {
    if (!selectedCast || !game) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/club-games/${game.id}/submit`, null, {
        params: { user_id: user.id, cast_id: selectedCast.id, cast_name: selectedCast.name }
      });
      setUserEntry(res.data.entry);
      toast.success(`Prediction submitted: ${selectedCast.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoScore = async () => {
    if (!game) return;
    setScoring(true);
    try {
      const res = await axios.post(`${API_URL}/api/club-games/${game.id}/auto-score`, null, {
        params: { user_id: user.id }
      });
      if (res.data.status === 'pending') {
        toast.info(res.data.message);
        setGame(prev => ({ ...prev, status: 'pending_results' }));
      } else {
        toast.success(res.data.message);
        loadGame();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-score failed');
    } finally {
      setScoring(false);
    }
  };

  const handleManualComplete = async () => {
    if (survivors.length === 0 || !game) return;
    setCompleting(true);
    try {
      await axios.post(`${API_URL}/api/club-games/${game.id}/complete`, null, {
        params: {
          user_id: user.id,
          survivor_cast_ids: survivors.map(s => s.id).join(','),
          survivor_cast_names: survivors.map(s => s.name).join(','),
        }
      });
      toast.success(`Game completed! ${survivors.length} survivors selected.`);
      loadGame();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete game');
    } finally {
      setCompleting(false);
    }
  };

  const toggleSurvivor = (member) => {
    setSurvivors(prev => {
      const exists = prev.find(s => s.id === member.id);
      if (exists) return prev.filter(s => s.id !== member.id);
      return [...prev, member];
    });
  };

  if (!isOpen) return null;

  const TMDB_IMG = 'https://image.tmdb.org/t/p/w200';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md bg-zinc-900 border-zinc-700 text-white p-0 max-h-[90dvh] overflow-y-auto rounded-xl" style={{ gap: 0 }}>
        <DialogTitle className="sr-only">Last Person Standing</DialogTitle>
        <DialogDescription className="sr-only">Elimination show prediction game</DialogDescription>

        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 z-10 px-4 py-3 border-b border-zinc-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-indigo-800 flex items-center justify-center">
                <Gamepad2 className="h-4 w-4 text-indigo-200" />
              </div>
              <div>
                <h2 className="text-sm font-bold" data-testid="lps-title">Last Person Standing</h2>
                <p className="text-[10px] text-gray-500 truncate max-w-[200px]">{showName}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-700 text-gray-400" data-testid="lps-close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-7 w-7 border-2 border-indigo-600 border-t-transparent rounded-full" />
            </div>
          ) : !game ? (
            /* ====== NO GAME — Start ====== */
            <div className="text-center py-6 space-y-4" data-testid="lps-no-game">
              {posterPath && (
                <img src={`${TMDB_IMG}${posterPath}`} alt={showName} className="w-28 aspect-[2/3] object-cover rounded-lg mx-auto" />
              )}
              <div>
                <p className="text-sm text-gray-300">No active game for this show yet.</p>
                <p className="text-xs text-gray-500 mt-1">Start the game to become the administrator.</p>
              </div>
              <Button
                onClick={handleCreateGame}
                disabled={submitting}
                className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
                data-testid="lps-start-game-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gamepad2 className="h-4 w-4" />}
                Start Game
              </Button>
            </div>

          ) : isCompleted && leaderboard ? (
            /* ====== COMPLETED — Results Summary + Leaderboard ====== */
            <div className="space-y-3" data-testid="lps-completed">
              <div className="text-center py-2">
                <Trophy className="h-8 w-8 text-amber-400 mx-auto mb-1" />
                <p className="text-sm font-semibold text-white">Game Complete</p>
                {game.season_number > 0 && (
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    S{game.season_number}E{game.episode_number}
                    {game.episode_title && <span> — {game.episode_title}</span>}
                  </p>
                )}
              </div>

              {/* Post-Episode Results Summary */}
              {gameResult?.cast_results && gameResult.cast_results.length > 0 && (
                <div className="bg-zinc-800/60 rounded-lg p-3 space-y-2" data-testid="lps-results-summary">
                  <p className="text-[11px] text-gray-400 font-medium text-center uppercase tracking-wider">Episode Results</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {gameResult.cast_results.map(member => (
                      <div
                        key={member.id}
                        className={`relative flex flex-col items-center p-2 rounded-lg transition-all ${
                          member.survived
                            ? 'bg-green-950/40 border border-green-800/40'
                            : 'bg-red-950/30 border border-red-900/30 opacity-60'
                        }`}
                        data-testid={`lps-result-${member.id}`}
                      >
                        {/* Status badge */}
                        <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center z-10 ${
                          member.survived ? 'bg-green-600' : 'bg-red-600'
                        }`}>
                          {member.survived
                            ? <Check className="h-3 w-3 text-white" />
                            : <X className="h-3 w-3 text-white" />
                          }
                        </div>
                        {/* Avatar */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold overflow-hidden mb-1 ${
                          member.survived ? 'ring-2 ring-green-600/50' : 'ring-1 ring-red-800/40 grayscale'
                        } bg-zinc-700`}>
                          {member.image
                            ? <img src={member.image} alt="" className="w-full h-full object-cover" />
                            : member.name.charAt(0).toUpperCase()}
                        </div>
                        {/* Name */}
                        <p className={`text-[10px] leading-tight text-center font-medium line-clamp-2 ${
                          member.survived ? 'text-white' : 'text-gray-500 line-through'
                        }`}>{member.name}</p>
                        <p className={`text-[8px] mt-0.5 ${member.survived ? 'text-green-400' : 'text-red-400'}`}>
                          {member.survived ? 'SAFE' : 'OUT'}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-center gap-4 text-[10px] text-gray-500 pt-1">
                    <span><span className="text-green-400 font-semibold">{gameResult.cast_results.filter(m => m.survived).length}</span> survived</span>
                    <span><span className="text-red-400 font-semibold">{gameResult.cast_results.filter(m => !m.survived).length}</span> eliminated</span>
                  </div>
                </div>
              )}

              {/* Fallback text if no cast_results */}
              {(!gameResult?.cast_results || gameResult.cast_results.length === 0) && (
                <>
                  {gameResult?.eliminated_cast_names && gameResult.eliminated_cast_names.length > 0 && (
                    <p className="text-xs text-red-400 text-center">
                      Eliminated: <span className="font-bold text-white">{gameResult.eliminated_cast_names.join(', ')}</span>
                    </p>
                  )}
                  {gameResult?.survivor_cast_names && gameResult.survivor_cast_names.length > 0 && (
                    <p className="text-xs text-green-400 text-center">
                      Survived: <span className="font-bold text-white">{gameResult.survivor_cast_names.join(', ')}</span>
                    </p>
                  )}
                  {gameResult?.winning_cast_name && !gameResult?.survivor_cast_names?.length && (
                    <p className="text-xs text-green-400 text-center">
                      Last Person Standing: <span className="font-bold text-white">{gameResult.winning_cast_name}</span>
                    </p>
                  )}
                </>
              )}

              {/* Leaderboard */}
              <p className="text-[11px] text-gray-400 font-medium text-center uppercase tracking-wider pt-1">Predictions</p>
              <div className="space-y-1" data-testid="lps-leaderboard">
                {leaderboard.map((entry, i) => (
                  <div
                    key={entry.user_id}
                    className={`flex items-center gap-2 p-2.5 rounded-lg ${
                      entry.user_id === user?.id ? 'bg-indigo-900/20 border border-indigo-700/40' :
                      i === 0 && entry.is_correct ? 'bg-amber-900/20 border border-amber-800/40' : 'bg-zinc-800'
                    }`}
                    data-testid={`lps-lb-entry-${i}`}
                  >
                    <div className="w-6 flex justify-center flex-shrink-0">{getRankIcon(entry.rank)}</div>
                    <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-[11px] font-semibold overflow-hidden flex-shrink-0">
                      {entry.profile_picture
                        ? <img src={entry.profile_picture} alt="" className="w-full h-full object-cover" />
                        : (entry.display_name?.[0] || '?').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white font-medium truncate">
                        {entry.display_name || `@${entry.username}`}
                        {entry.user_id === user?.id && <span className="text-[9px] text-gray-500 ml-1">(you)</span>}
                      </p>
                      <p className="text-[10px] text-gray-500 truncate">
                        Picked: <span className={entry.is_correct ? 'text-green-400' : 'text-red-400'}>{entry.selected_cast_name}</span>
                        {entry.is_correct && <Check className="inline h-3 w-3 text-green-400 ml-0.5" />}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-sm font-bold ${entry.points > 0 ? 'text-amber-400' : 'text-gray-600'}`}>{entry.points}</p>
                      <p className="text-[9px] text-gray-600">{formatMs(entry.submission_time_ms)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          ) : isPending ? (
            /* ====== PENDING RESULTS ====== */
            <div className="text-center py-6 space-y-3" data-testid="lps-pending">
              <AlertCircle className="h-10 w-10 text-amber-400 mx-auto" />
              <p className="text-sm font-semibold text-white">Results Pending</p>
              <p className="text-xs text-gray-400">Next episode cast is not available yet. Results will be calculated when data becomes available.</p>
              {isAdmin && (
                <div className="mt-3 space-y-2">
                  <Button onClick={handleAutoScore} disabled={scoring} variant="outline" className="border-amber-700 text-amber-400 gap-1.5">
                    {scoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gamepad2 className="h-4 w-4" />}
                    Retry Auto-Score
                  </Button>
                </div>
              )}
            </div>

          ) : hasSubmitted ? (
            /* ====== SUBMITTED — Waiting ====== */
            <div className="text-center py-6 space-y-3" data-testid="lps-waiting">
              <div className="w-14 h-14 rounded-full bg-green-900/30 border border-green-700/40 flex items-center justify-center mx-auto">
                <Check className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Prediction Submitted</p>
                <p className="text-xs text-gray-400 mt-1">
                  You selected: <span className="text-white font-medium">{userEntry.selected_cast_name}</span>
                </p>
                <p className="text-[10px] text-gray-600 mt-0.5">{formatMs(userEntry.submission_time_ms)}</p>
              </div>

              {/* Episode countdown for submitted state */}
              {game.season_number > 0 && (
                <div className="bg-zinc-800 rounded-lg p-3 space-y-1.5" data-testid="lps-waiting-episode">
                  <p className="text-[11px] text-gray-400 font-medium">
                    S{game.season_number}E{game.episode_number}
                    {game.episode_title && <span className="text-gray-300"> — {game.episode_title}</span>}
                  </p>
                  {countdown !== null && (
                    countdown <= 0 ? (
                      <p className="text-xs text-amber-400 font-semibold">Episode has aired — Awaiting results</p>
                    ) : (
                      <div className="flex items-center justify-center gap-1.5 text-amber-400">
                        <Clock className="h-3.5 w-3.5" />
                        <span className="text-xs font-mono font-bold">{formatCountdown(countdown)}</span>
                        <span className="text-[10px] text-gray-500">until episode airs</span>
                      </div>
                    )
                  )}
                </div>
              )}

              <div className="bg-zinc-800 rounded-lg p-3">
                <Clock className="h-4 w-4 text-amber-400 mx-auto mb-1" />
                <p className="text-[11px] text-gray-400">Waiting for game administrator to complete the game</p>
                <p className="text-[10px] text-gray-600 mt-1">{game.entry_count || 0} predictions so far</p>
              </div>

              {/* Admin controls */}
              {isAdmin && (
                <div className="mt-4 border-t border-zinc-700 pt-4">
                  <button
                    onClick={() => setShowAdminPanel(!showAdminPanel)}
                    className="flex items-center gap-1.5 mx-auto text-xs text-amber-400 hover:text-amber-300"
                    data-testid="lps-admin-toggle"
                  >
                    <Shield className="h-3.5 w-3.5" />
                    Admin: Score Game
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAdminPanel ? 'rotate-180' : ''}`} />
                  </button>
                  {showAdminPanel && (
                    <div className="mt-3 space-y-3 text-left" data-testid="lps-admin-panel">
                      {/* Mode toggle */}
                      <div className="flex gap-1">
                        <button onClick={() => setAdminMode('auto')} className={`flex-1 py-1.5 text-xs rounded-md font-medium ${adminMode === 'auto' ? 'bg-indigo-600 text-white' : 'bg-zinc-800 text-gray-400'}`}>
                          Auto (TVMaze)
                        </button>
                        <button onClick={() => setAdminMode('manual')} className={`flex-1 py-1.5 text-xs rounded-md font-medium ${adminMode === 'manual' ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-gray-400'}`}>
                          Manual
                        </button>
                      </div>

                      {adminMode === 'auto' ? (
                        <div className="space-y-2">
                          <p className="text-[11px] text-gray-400">Compare current vs next episode cast from TVMaze to auto-detect eliminations.</p>
                          <Button
                            onClick={handleAutoScore}
                            disabled={scoring}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
                            data-testid="lps-auto-score-btn"
                          >
                            {scoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gamepad2 className="h-4 w-4" />}
                            Auto-Score via TVMaze
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-[11px] text-gray-400">Select all cast members who <span className="text-green-400 font-semibold">survived</span> the episode:</p>
                          <div className="space-y-1 max-h-[30vh] overflow-y-auto pr-1" data-testid="lps-survivor-list">
                            {cast.map(c => {
                              const isSurvivor = survivors.find(s => s.id === c.id);
                              return (
                                <button
                                  key={c.id}
                                  onClick={() => toggleSurvivor(c)}
                                  className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-all ${
                                    isSurvivor
                                      ? 'bg-green-900/30 border border-green-700/50'
                                      : 'bg-zinc-800 hover:bg-zinc-750 border border-transparent'
                                  }`}
                                  data-testid={`lps-survivor-${c.id}`}
                                >
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                    isSurvivor ? 'bg-green-600 border-green-600' : 'border-zinc-600'
                                  }`}>
                                    {isSurvivor && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] font-semibold overflow-hidden flex-shrink-0">
                                    {c.image
                                      ? <img src={c.image} alt="" className="w-full h-full object-cover" />
                                      : c.name.charAt(0).toUpperCase()}
                                  </div>
                                  <span className="text-xs text-white truncate flex-1">{c.name}</span>
                                </button>
                              );
                            })}
                          </div>
                          {survivors.length > 0 && (
                            <p className="text-[10px] text-green-400">{survivors.length} survivor{survivors.length !== 1 ? 's' : ''} selected</p>
                          )}
                          <Button
                            onClick={handleManualComplete}
                            disabled={survivors.length === 0 || completing}
                            className="w-full bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                            data-testid="lps-complete-game-btn"
                          >
                            {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />}
                            Complete Game ({survivors.length} survivor{survivors.length !== 1 ? 's' : ''})
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

          ) : (
            /* ====== ACTIVE — Select cast ====== */
            <div className="space-y-3" data-testid="lps-select">
              {/* Episode info & countdown */}
              {game.season_number > 0 && (
                <div className="bg-zinc-800 rounded-lg p-3 text-center space-y-1.5" data-testid="lps-episode-info">
                  <p className="text-[11px] text-gray-400 font-medium">
                    S{game.season_number}E{game.episode_number}
                    {game.episode_title && <span className="text-gray-300"> — {game.episode_title}</span>}
                  </p>
                  {countdown !== null && (
                    <div data-testid="lps-countdown">
                      {predictionsLocked ? (
                        <div className="flex items-center justify-center gap-1.5 text-red-400">
                          <AlertCircle className="h-3.5 w-3.5" />
                          <span className="text-xs font-semibold">Episode has aired — Predictions locked</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1.5 text-amber-400">
                          <Clock className="h-3.5 w-3.5" />
                          <span className="text-xs font-mono font-bold">{formatCountdown(countdown)}</span>
                          <span className="text-[10px] text-gray-500">until episode airs</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="text-center">
                <p className="text-sm font-semibold text-indigo-400">Who will be the last person standing?</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Select one guest cast member you believe will survive</p>
                {castSource && <p className="text-[9px] text-gray-600 mt-0.5">Cast source: {castSource}</p>}
              </div>

              {cast.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-xs">Guest cast data not available for this episode yet.</p>
                  <p className="text-[10px] text-gray-600 mt-1">TVMaze may not have published the guest cast. Check back closer to air time.</p>
                </div>
              ) : (
                <div className={`space-y-1 max-h-[40vh] overflow-y-auto pr-1 ${predictionsLocked ? 'opacity-50 pointer-events-none' : ''}`} data-testid="lps-cast-list">
                  {cast.map(member => {
                    const isSelected = selectedCast?.id === member.id;
                    return (
                      <button
                        key={member.id}
                        onClick={() => !predictionsLocked && setSelectedCast(isSelected ? null : member)}
                        disabled={predictionsLocked}
                        className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-all ${
                          isSelected
                            ? 'bg-indigo-900/30 border border-indigo-700/50 ring-1 ring-indigo-700/30'
                            : 'bg-zinc-800 hover:bg-zinc-750 border border-transparent'
                        }`}
                        data-testid={`lps-cast-${member.id}`}
                      >
                        <div className="w-9 h-9 rounded-full bg-zinc-700 flex items-center justify-center text-xs font-semibold overflow-hidden flex-shrink-0">
                          {member.image
                            ? <img src={member.image} alt="" className="w-full h-full object-cover" />
                            : member.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white font-medium truncate">{member.name}</p>
                          {member.character && <p className="text-[10px] text-gray-500 truncate">as {member.character}</p>}
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {selectedCast && !predictionsLocked && (
                <div className="sticky bottom-0 bg-zinc-900 pt-2 border-t border-zinc-700">
                  <Button
                    onClick={handleSubmitPrediction}
                    disabled={submitting}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
                    data-testid="lps-submit-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gamepad2 className="h-4 w-4" />}
                    Lock In: {selectedCast.name}
                  </Button>
                </div>
              )}

              <p className="text-center text-[10px] text-gray-600">{game.entry_count || 0} predictions submitted</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LastPersonStanding;
